import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import {
  sendSingleNotificationService,
  sendBulkNotificationService,
  fetchAllNotificationsService,
} from "../../apis/notifications";
import { cleanObject } from "../../helpers";

interface NotificationState {
  allNotifications: any[];
  loading: boolean;
  error: string | null;
  sendingSingle: boolean;
  sendingBulk: boolean;
}

const initialState: NotificationState = {
  allNotifications: [],
  loading: false,
  error: null,
  sendingSingle: false,
  sendingBulk: false,
};

// Fetch all notifications
export const fetchAllNotifications = createAsyncThunk(
  "notifications/fetchAll",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await fetchAllNotificationsService(cleanObject(payload));
      return response?.data;
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ?? "Failed to fetch notifications"
      );
      return rejectWithValue(error.response?.data?.message ?? "Fetch failed");
    }
  }
);

// Send single notification
export const sendSingleNotification = createAsyncThunk(
  "notifications/sendSingle",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await sendSingleNotificationService(payload);
      toast.success("Single notification sent successfully");
      return response?.data;
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ?? "Failed to send notification"
      );
      return rejectWithValue(error.response?.data?.message ?? "Send failed");
    }
  }
);

// Send bulk notification
export const sendBulkNotification = createAsyncThunk(
  "notifications/sendBulk",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await sendBulkNotificationService(payload);
      toast.success("Bulk notification sent successfully");
      return response?.data;
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ?? "Failed to send notification"
      );
      return rejectWithValue(error.response?.data?.message ?? "Send failed");
    }
  }
);

const notificationSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    resetNotifications: (state) => {
      state.allNotifications = [];
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all
      .addCase(fetchAllNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.allNotifications = action.payload;
      })
      .addCase(
        fetchAllNotifications.rejected,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload;
        }
      )

      // Send single
      .addCase(sendSingleNotification.pending, (state) => {
        state.sendingSingle = true;
        state.error = null;
      })
      .addCase(sendSingleNotification.fulfilled, (state, action) => {
        state.sendingSingle = false;
      })
      .addCase(
        sendSingleNotification.rejected,
        (state, action: PayloadAction<any>) => {
          state.sendingSingle = false;
          state.error = action.payload;
        }
      )

      // Send bulk
      .addCase(sendBulkNotification.pending, (state) => {
        state.sendingBulk = true;
        state.error = null;
      })
      .addCase(sendBulkNotification.fulfilled, (state, action) => {
        state.sendingBulk = false;
      })
      .addCase(
        sendBulkNotification.rejected,
        (state, action: PayloadAction<any>) => {
          state.sendingBulk = false;
          state.error = action.payload;
        }
      );
  },
});

export const { resetNotifications } = notificationSlice.actions;
export default notificationSlice.reducer;
